import { LoadingBar } from '@components/Loading/Loading'
import {
	Box,
	Form,
	IconSolidShieldCheck,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { useEffect, useState } from 'react'

import BorderBox from '@/components/BorderBox/BorderBox'
import BoxLabel from '@/components/BoxLabel/BoxLabel'
import { useProjectSettingsContext } from '@/pages/ProjectSettings/ProjectSettingsContext/ProjectSettingsContext'

import styles from './TrafficExclusionForm.module.css'

const parseLines = (value: string) =>
	value
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean)

export const TrafficExclusionForm = () => {
	const {
		allProjectSettings: data,
		loading,
		setAllProjectSettings,
	} = useProjectSettingsContext()

	const settings = data?.projectSettings
	const excludedIPs = settings?.excluded_ips ?? []
	const [excludedIPText, setExcludedIPText] = useState(excludedIPs.join('\n'))

	useEffect(() => {
		setExcludedIPText(excludedIPs.join('\n'))
	}, [settings?.id])

	if (loading) {
		return <LoadingBar />
	}

	const updateTrafficExclusion = (
		updates: Partial<
			Pick<
				NonNullable<typeof settings>,
				'excluded_ips' | 'excluded_user_agent' | 'excluded_cookie_name'
			>
		>,
	) => {
		setAllProjectSettings((currentProjectSettings) =>
			currentProjectSettings?.projectSettings
				? {
						projectSettings: {
							...currentProjectSettings.projectSettings,
							...updates,
						},
					}
				: currentProjectSettings,
		)
	}

	return (
		<BorderBox>
			<Form>
				<Stack gap="12">
					<Box display="flex" gap="8" alignItems="flex-start">
						<Box pt="4">
							<IconSolidShieldCheck size={18} />
						</Box>
						<Stack gap="4">
							<BoxLabel
								label="Traffic exclusion"
								info="Drop matching visitors before a session is created. If any rule matches, session replay, frontend logs, network data, and errors from that visitor are not recorded."
							/>
							<Box display="flex" gap="6" flexWrap="wrap">
								<span className={styles.rulePill}>
									IP address
								</span>
								<span className={styles.rulePill}>OR</span>
								<span className={styles.rulePill}>
									User agent
								</span>
								<span className={styles.rulePill}>OR</span>
								<span className={styles.rulePill}>
									Cookie name
								</span>
							</Box>
						</Stack>
					</Box>

					<Form.NamedSection
						label="IP addresses"
						name="excluded_ips"
						optional
					>
						<textarea
							className={styles.textarea}
							name="excluded_ips"
							placeholder={'127.0.0.1\n10.0.0.0/8'}
							value={excludedIPText}
							onChange={(e) => {
								setExcludedIPText(e.target.value)
								updateTrafficExclusion({
									excluded_ips: parseLines(e.target.value),
								})
							}}
						/>
						<Text color="moderate" size="xSmall">
							One IPv4, IPv6, or CIDR range per line.
						</Text>
					</Form.NamedSection>

					<Box display="flex" gap="8" flexWrap="wrap">
						<Form.Input
							label="User agent contains"
							name="excluded_user_agent"
							placeholder="Googlebot"
							value={settings?.excluded_user_agent ?? ''}
							onChange={(e) => {
								updateTrafficExclusion({
									excluded_user_agent: e.target.value,
								})
							}}
						/>
						<Form.Input
							label="Cookie name"
							name="excluded_cookie_name"
							placeholder="internal_monitor"
							value={settings?.excluded_cookie_name ?? ''}
							onChange={(e) => {
								updateTrafficExclusion({
									excluded_cookie_name: e.target.value,
								})
							}}
						/>
					</Box>
				</Stack>
			</Form>
		</BorderBox>
	)
}
